import React, { useState } from 'react';
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Download, Gift, Plus, X } from 'lucide-react';
import ToolCard, { ToolButton, toolTextSx } from './ToolCard';
import DopamenuImportDialog from './DopamenuImportDialog';
import { MAX_REWARDS, MAX_REWARD_LENGTH } from '../utils/usePomodoro';

export default function Dopamenu({ rewards, onAdd, onRemove, onImport }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const addReward = (event) => {
    event.preventDefault();
    const reward = draft.trim();
    if (!reward) return;
    if (rewards.some((item) => item.toLowerCase() === reward.toLowerCase())) {
      setError('Already on your menu.');
      return;
    }
    if (rewards.length >= MAX_REWARDS) {
      setError('Remove a reward to add another.');
      return;
    }
    onAdd(reward);
    setDraft('');
    setError('');
  };
  return (
    <ToolCard id="dopamenu" title="Timer rewards" icon={<Gift size={22} />}>
      <Typography sx={{ ...toolTextSx, textAlign: 'center', color: 'text.primary', mb: 2.5 }}>Finish your timer. Get a random reward.</Typography>
      <ToolButton onClick={() => setImportOpen(true)} startIcon={<Download size={18} />} sx={{ mb: 3, fontSize: '0.6rem' }}>Import from dopamenu</ToolButton>
      <Stack component="form" onSubmit={addReward} direction="row" alignItems="flex-start" gap={1.5}>
        <TextField label="Add a reward" placeholder="Something you enjoy" value={draft}
          onChange={(event) => { setDraft(event.target.value); setError(''); }} fullWidth
          inputProps={{ maxLength: MAX_REWARD_LENGTH }} error={Boolean(error)} helperText={error}
          FormHelperTextProps={{ sx: toolTextSx }} InputLabelProps={{ sx: { fontSize: { xs: '0.65rem', sm: '0.65rem' } } }}
          sx={{ '& input': { fontSize: '0.75rem' }, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
        <ToolButton type="submit" aria-label="Add reward" variant="contained" disabled={!draft.trim()} sx={{ minWidth: 48, px: 0, height: 54 }}><Plus size={22} aria-hidden="true" /></ToolButton>
      </Stack>
      {rewards.length ? (
        <Box component="ul" aria-label="Saved rewards" sx={{ listStyle: 'none', p: 0, mt: 3, mb: 0, maxHeight: 380, overflowY: 'auto' }}>
          {rewards.map((reward) => (
            <Stack component="li" key={reward} direction="row" alignItems="center" gap={1.5} sx={(theme) => ({
              pl: 2, pr: 0.5, py: 0.5, mb: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              borderRadius: 4, background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
            })}>
              <Typography sx={{ ...toolTextSx, flex: 1, minWidth: 0, overflowWrap: 'anywhere', textAlign: 'left' }}>{reward}</Typography>
              <IconButton aria-label={`Remove ${reward}`} onClick={() => onRemove(reward)} sx={{ color: 'secondary.main', width: 44, height: 44, flexShrink: 0 }}><X size={16} aria-hidden="true" /></IconButton>
            </Stack>
          ))}
        </Box>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ ...toolTextSx, mb: 1.5, color: 'secondary.main' }}>Try one:</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {['Stretch', 'A favorite song', 'Make tea'].map((reward) => <ToolButton key={reward} onClick={() => onAdd(reward)} sx={{ fontSize: '0.6rem', px: 1.5 }}>{reward}</ToolButton>)}
          </Stack>
        </Box>
      )}
      {importOpen && <DopamenuImportDialog rewards={rewards} onImport={onImport} onClose={() => setImportOpen(false)} />}
    </ToolCard>
  );
}
